import { useMemo, useRef } from 'react';

type RegistryState = {
  contextId: string | null;
  seenKeys: Set<string>;
};

const EMPTY_ENTERING_KEYS = new Set<string>();

function compactSeenKeys(seenKeys: Set<string>, currentKeys: readonly string[]): void {
  const current = new Set(currentKeys);
  for (const key of seenKeys) {
    if (!current.has(key)) {
      seenKeys.delete(key);
    }
  }
}

/**
 * Анимация сообщения привязана к logical key, а не к React mount.
 * Первый снимок контекста считается уже показанным; анимируются только новые ключи.
 */
export function useMessengerMessageEnterRegistry(
  contextId: string,
  logicalKeys: readonly string[],
  enabled = true,
): ReadonlySet<string> {
  const registryRef = useRef<RegistryState>({
    contextId: null,
    seenKeys: new Set(),
  });

  return useMemo(() => {
    if (!enabled) return EMPTY_ENTERING_KEYS;

    const registry = registryRef.current;
    if (registry.contextId !== contextId) {
      registry.contextId = contextId;
      registry.seenKeys = new Set(logicalKeys);
      return EMPTY_ENTERING_KEYS;
    }

    const enteringKeys = new Set<string>();
    for (const key of logicalKeys) {
      if (!registry.seenKeys.has(key)) {
        registry.seenKeys.add(key);
        enteringKeys.add(key);
      }
    }
    compactSeenKeys(registry.seenKeys, logicalKeys);
    return enteringKeys.size > 0 ? enteringKeys : EMPTY_ENTERING_KEYS;
  }, [contextId, enabled, logicalKeys]);
}
